import { useState, type ReactNode } from "react";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface InfoHintProps {
  /** Accessible description of what the tooltip explains. */
  label: string;
  children: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
  contentClassName?: string;
}

/**
 * Consistent info tooltip for Finances metrics (AR-211): a small muted Info
 * icon placed directly next to a metric / section name, rather than in the
 * top-right corner of the container.
 *
 * AR-316: also works on touch. A tooltip that only opens on hover is
 * unreachable on a phone, so the icon toggles the tooltip on tap (Radix
 * dismisses on pointerdown by default, hence the preventDefault) while hover
 * and keyboard focus keep working unchanged on pointer devices. The trigger is
 * given a taller hit area on small screens (vertical only, so it can never
 * widen its container) and the content is kept inside the viewport
 * with a collision padding.
 */
export function InfoHint({
  label,
  children,
  side = "top",
  className,
  contentClassName,
}: InfoHintProps) {
  const [open, setOpen] = useState(false);

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label}
            aria-expanded={open}
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => setOpen((prev) => !prev)}
            className={cn(
              "relative inline-flex shrink-0 rounded-sm text-muted-foreground/50 transition-colors before:absolute before:-inset-y-3 before:-inset-x-1 before:content-[''] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 lg:before:content-none",
              className,
            )}
          >
            <Info className="h-3.5 w-3.5" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          className={cn("max-w-[280px] text-xs sm:max-w-[320px]", contentClassName)}
        >
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
